import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import isMobilePhone from 'validator/es/lib/isMobilePhone';
import * as Yup from 'yup';
import { Button, FieldWrapper, FormError, Identity, Input, MobileVerificationForm, Modal, ModalContent, ModalFooter, ModalFormLayout, ModalHeader, ModalTitle } from 'components';
import { Size } from 'design/types';
import { mobileLocale } from 'lib/Validations';
import Form from 'screens/Error/Form';
import { clearRequest, selectAPIRequestErrors, selectAPIRequestStatus, selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectEmployers } from 'store/Employers';
import { IStore } from 'store/types';
import { resendIdentityVerificationCodeRequest, selectCurrentUserIdentities, selectVerifiedIdentities, updateIdentityVerificationCodeRequest, updateSMSRequest, User } from 'store/User';
import { ChannelType, IApiErrorPayloadWithCode, IdentityType, PermissionType, RequestStatus } from 'types';
import { hasSitePermission } from 'utils/employers';
import { getIdentityByType } from 'utils/identity';
import { formatMobile } from 'utils/strings';
import { MissingMethodMessage, SettingsFieldWrapper, SettingsSectionContent, SettingsTag } from './styles';

const formatError = (error: string) => {
  if (error.toLowerCase().trim() === 'this contact is already in use on another account.') {
    return 'The number you entered is already in use on your account.';
  }
  return error;
};

const FormMobile: React.FC = () => {
  const dispatch = useDispatch();
  const [showError, setShowError] = useState(false);
  type UpdateScreen = 'EnterMobile' | 'EnterVerification';
  const [modalActive, setModalActive] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<UpdateScreen>('EnterMobile');
  const [mobile, setMobile] = useState<string>('');
  const identities = useSelector(selectCurrentUserIdentities());
  const employers = useSelector(selectEmployers);
  const currentEmployers = Object.values(employers);
  const paymentReviewPermission = useMemo(() => currentEmployers.some((org) => Object.values(org.sites).some((site) => hasSitePermission(site.permissions, PermissionType.PAYMENT_APPROVAL))), [currentEmployers]);

  const mobileErrorMessages = (useSelector((state: IStore) => selectAPIRequestErrors(state, User.UPDATE_SMS_REQUEST)) as IApiErrorPayloadWithCode)?.errorMessages;
  const apiErrorsVerify = useSelector((state: IStore) => selectAPIRequestErrors(state, User.UPDATE_IDENTITY_VERIFICATION_CODE_REQUEST)) as IApiErrorPayloadWithCode;
  const userLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.UPDATE_SMS_REQUEST));
  const verifyLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.UPDATE_IDENTITY_VERIFICATION_CODE_REQUEST));
  const resendVerifyLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, User.RESEND_IDENTITY_VERIFICATION_CODE_REQUEST));
  const status = useSelector((state: IStore) => selectAPIRequestStatus(state, User.UPDATE_SMS_REQUEST));
  const verifyStatus = useSelector((state: IStore) => selectAPIRequestStatus(state, User.UPDATE_IDENTITY_VERIFICATION_CODE_REQUEST));
  const currentUserIdentities = useSelector(selectVerifiedIdentities());
  const [hasResentPasscode, setHasResentPasscode] = useState<boolean>(false);

  const currentPhoneIdentity = getIdentityByType(currentUserIdentities, IdentityType.MOBILE)?.value;
  const noPhoneNumber = !currentPhoneIdentity;

  useEffect(() => {
    if (mobileErrorMessages && mobileErrorMessages.length > 0) {
      setShowError(true);
    }
  }, [mobileErrorMessages]);

  const handleMobileUpdateClick = () => {
    setModalActive(true);
    setModalContent('EnterMobile');
  };

  const updateIdentity = (identityValue: string, toastMessage?: string) => {
    dispatch(updateSMSRequest(identityValue, toastMessage));
  };

  const validationSchema = Yup.object().shape({
    identity: Yup.string()
      .test('isMobilePhone', 'Invalid mobile number.', (phoneNumber: string) => isMobilePhone(phoneNumber || '', mobileLocale))
      .test('sameMobilePhone', 'This is your current mobile number.', (phoneNumber: string) => phoneNumber !== currentPhoneIdentity)
      .required(),
  });

  const { errors, touched, isValid, dirty, setFieldValue, handleSubmit, resetForm, values, handleBlur } = useFormik({
    initialValues: {
      identity: '',
    },
    onSubmit: () => {
      updateIdentity(values.identity);
    },
    validateOnBlur: true,
    validationSchema,
    enableReinitialize: true,
  });

  const onVerifySubmit = (verificationCode: string) => {
    const mobileIdentity = (identities || []).find((identity) => identity.value === values.identity);
    dispatch(updateIdentityVerificationCodeRequest(mobileIdentity?.id, verificationCode, 'Mobile updated successfully'));
  };

  const resendSms = () => {
    updateIdentity(values.identity, 'Verification code resent successfully');
    setHasResentPasscode(true);
  };

  const resendCall = () => {
    const newIdentity = identities.find((identity) => identity.value === values.identity);

    if (newIdentity) {
      dispatch(resendIdentityVerificationCodeRequest(newIdentity.id, 'Verification call initiated', ChannelType.CALL));
    }
  };

  useEffect(() => {
    if (status === RequestStatus.SUCCESS) {
      setMobile(values.identity);
      setModalContent('EnterVerification');
    }
  }, [status, values.identity]);

  useEffect(() => {
    if (verifyStatus === RequestStatus.SUCCESS) {
      setModalActive(false);
      resetForm();
    }
  }, [verifyStatus, resetForm]);

  const validateField = (field: 'identity') => Boolean(touched[field] && errors[field]);

  const verificationForm = (
    <MobileVerificationForm
      mobile={mobile}
      isResendLoading={userLoading || resendVerifyLoading}
      isLoading={verifyLoading}
      errorMessages={apiErrorsVerify?.errorMessages}
      onSubmit={onVerifySubmit}
      resendVerificationCode={resendSms}
      resendCall={resendCall}
      showCallButton={hasResentPasscode}
    />
  );

  const enterMobileForm = (
    <>
      <ModalHeader>
        <ModalTitle>{noPhoneNumber ? 'Add ' : 'Change '} Mobile Number</ModalTitle>
      </ModalHeader>

      <ModalContent>
        <Form formId={User.UPDATE_SMS_REQUEST} onSubmit={handleSubmit} onChange={() => setShowError(false)}>
          <ModalFormLayout>
            <FieldWrapper label="Mobile Number" message={validateField('identity') ? errors.identity : ''} hasError={validateField('identity')}>
              <Identity
                id="identity"
                disabled={userLoading}
                value={values.identity}
                onBlur={handleBlur}
                onAccept={(val: string) => {
                  setFieldValue('identity', val);
                }}
                type="text"
                placeholder="Mobile Number"
                inputMode="numeric"
                phoneOnly
              />
            </FieldWrapper>
          </ModalFormLayout>

          <ModalFooter columns={1}>
            {showError && mobileErrorMessages && mobileErrorMessages.map((error, index) => <FormError key={`error-message-${index}`}>{formatError(error)}</FormError>)}
            <Button type="submit" appearance="primary" size="large" label="Next" disabled={userLoading || !(isValid && dirty)} isProcessing={userLoading} data-cy="mobile-modal-submit-button" />
          </ModalFooter>
        </Form>
      </ModalContent>
    </>
  );

  return (
    <>
      <Modal
        maxWidth={475}
        isActive={modalActive}
        onClose={() => {
          resetForm();
          setHasResentPasscode(false);
          setModalActive(!modalActive);
          dispatch(clearRequest(User.UPDATE_IDENTITY_VERIFICATION_CODE_REQUEST));
        }}
      >
        <>
          {modalContent === 'EnterVerification' && verificationForm}
          {modalContent === 'EnterMobile' && enterMobileForm}
        </>
      </Modal>

      <form data-cy="account-settings-contact-mobile-form">
        {paymentReviewPermission && <SettingsTag size={Size.SMALL} label="This number cannot be changed. Please contact Support to change this number." dataCy="mobile-edit-disabled-tag" />}
        <SettingsSectionContent columns="auto">
          <SettingsFieldWrapper label="Mobile" data-cy="mobile-field">
            {noPhoneNumber && <MissingMethodMessage>Add a mobile number to make your account more secure. You will be able to log in with this number too.</MissingMethodMessage>}
            {currentPhoneIdentity && <Input readOnly type="text" value={currentPhoneIdentity && formatMobile(currentPhoneIdentity)} />}
          </SettingsFieldWrapper>

          <Button label={noPhoneNumber ? 'Add' : 'Edit'} type="button" appearance="primary" size="small" onClick={handleMobileUpdateClick} disabled={paymentReviewPermission} data-cy="change-mobile-button" />
        </SettingsSectionContent>
      </form>
    </>
  );
};

export default FormMobile;
